export interface BookingIntentLineageScope {
  actorId: string;
  clientId: string;
}

const opaqueKey = /^[A-Za-z0-9_-]{1,128}$/;
const intentKey =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const unavailable = () => new Error('Booking continuity could not be saved.');

export function isBookingIntentSourceKey(value: unknown): value is string {
  return (
    typeof value === 'string' && value !== 'default' && opaqueKey.test(value)
  );
}

function storageKey(
  scope: BookingIntentLineageScope,
  kind: 'entry' | 'intent',
  key: string,
) {
  if (!scope.actorId || !scope.clientId || !isBookingIntentSourceKey(key)) {
    throw unavailable();
  }
  return `ths:booking-lineage:v1:${JSON.stringify([scope.actorId, scope.clientId, kind, key])}`;
}

function read(key: string, pattern: RegExp) {
  const value = window.localStorage.getItem(key);
  if (value !== null && !pattern.test(value)) throw unavailable();
  return value;
}

async function retain(key: string, value: string, pattern: RegExp) {
  if (!pattern.test(value) || !navigator.locks) throw unavailable();
  return navigator.locks.request(key, () => {
    const existing = read(key, pattern);
    if (existing !== null) return existing;
    window.localStorage.setItem(key, value);
    return value;
  });
}

/** Only opaque history/source IDs and server-issued intent IDs are retained. */
export function resolveBookingIntentSource(
  scope: BookingIntentLineageScope,
  entryKey: string,
  inheritedSourceKey?: string | null,
): string {
  // The initial "default" key is shared by direct loads in separate tabs.
  // The search route replaces that entry before it can establish lineage.
  if (entryKey === 'default' || inheritedSourceKey === null) {
    throw unavailable();
  }
  const existing = read(storageKey(scope, 'entry', entryKey), opaqueKey);
  if (existing !== null && !isBookingIntentSourceKey(existing))
    throw unavailable();
  if (
    inheritedSourceKey !== undefined &&
    (!isBookingIntentSourceKey(inheritedSourceKey) ||
      (existing !== null && existing !== inheritedSourceKey))
  ) {
    throw unavailable();
  }
  return existing ?? inheritedSourceKey ?? entryKey;
}

export async function retainBookingIntentSource(
  scope: BookingIntentLineageScope,
  entryKey: string,
  sourceKey: string,
): Promise<void> {
  if (entryKey === 'default' || sourceKey === 'default') throw unavailable();
  const winner = await retain(
    storageKey(scope, 'entry', entryKey),
    sourceKey,
    opaqueKey,
  );
  if (winner !== sourceKey) throw unavailable();
}

export function readBookingIntent(
  scope: BookingIntentLineageScope,
  sourceKey: string,
): string | null {
  return read(storageKey(scope, 'intent', sourceKey), intentKey);
}

/** Concurrent first quotes must adopt this winner before enabling Book. */
export function retainBookingIntent(
  scope: BookingIntentLineageScope,
  sourceKey: string,
  bookingIntentId: string,
): Promise<string> {
  return retain(
    storageKey(scope, 'intent', sourceKey),
    bookingIntentId.toLowerCase(),
    intentKey,
  );
}
